import Link from 'next/link';
import { useState } from 'react';
import { fraunces } from '../Landing/fonts';
import { MUTED, PAPER_CARD } from '../Landing/ui';
import FutureImage from '../FutureImage';
import BlogPostCard from './BlogPostCard';

const GRID =
  'tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-8 sm:tw-grid-cols-2 lg:tw-grid-cols-3';

const FILTERS = [
  { key: 'all', label: 'All posts' },
  { key: 'guide', label: 'Guides' },
  { key: 'news', label: 'Product news' },
];

function formatDate(createdAt) {
  // Pinned to UTC: the list is SSR'd, so server and client must format
  // identical strings or hydration mismatches.
  return new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Newest post runs as a full-width "latest dispatch" — an editorial lead
// story, not just the first cell of a uniform grid.
function LeadCard({ post }) {
  return (
    <Link
      href={post.href}
      prefetch={false}
      className="tw-group tw-mb-10 tw-block tw-no-underline"
    >
      <article
        className={`${PAPER_CARD} tw-overflow-hidden tw-rounded-2xl tw-shadow-[0_14px_32px_-18px_rgba(29,45,40,0.38)] tw-transition-all tw-duration-300 group-hover:tw-shadow-[0_28px_52px_-18px_rgba(29,45,40,0.55)] group-hover:-tw-translate-y-1 lg:tw-grid lg:tw-grid-cols-5`}
      >
        <div className="tw-relative tw-aspect-[16/9] tw-overflow-hidden lg:tw-col-span-3 lg:tw-aspect-auto lg:tw-min-h-[320px]">
          {post.thumb ? (
            <FutureImage
              src={post.thumb.url}
              alt=""
              fill
              sizes="(max-width: 992px) 100vw, 60vw"
              placeholder={post.thumb.blur ? 'blur' : undefined}
              blurDataURL={post.thumb.blur}
              className="tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-[1.03]"
            />
          ) : (
            <div className="tw-h-full tw-w-full tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          )}
        </div>
        <div className="tw-flex tw-flex-col tw-justify-center tw-p-6 sm:tw-p-8 lg:tw-col-span-2">
          <p className="tw-mb-3 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-primary-700 dark:tw-text-primary-300">
            Latest dispatch
          </p>
          <h2
            className={`${fraunces.className} tw-m-0 tw-text-2xl tw-font-semibold tw-leading-snug tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] group-hover:tw-text-primary-700 dark:group-hover:tw-text-primary-300 sm:tw-text-3xl`}
          >
            {post.title}
          </h2>
          {post.excerpt && (
            <p
              className={`${MUTED} tw-mb-0 tw-mt-3 tw-overflow-hidden tw-text-[15px] tw-leading-relaxed [-webkit-box-orient:vertical] [-webkit-line-clamp:3] [display:-webkit-box]`}
            >
              {post.excerpt}
            </p>
          )}
          <p className="tw-mb-0 tw-mt-4 tw-font-mono tw-text-[11px] tw-tracking-wide tw-text-[#8A958F]">
            {post.category === 'guide' ? 'Guide · ' : ''}
            {formatDate(post.createdAt)}
            {post.readTime
              ? ` · ${Math.max(1, Math.round(post.readTime))} min read`
              : ''}
          </p>
        </div>
      </article>
    </Link>
  );
}

// Fully static list (posts SSR'd by pages/blog/index.js) — every card link
// is in the crawled HTML; the category tabs only filter client-side.
export default function BlogPosts({ posts }) {
  const [filter, setFilter] = useState('all');

  const filtered =
    filter === 'all' ? posts : posts.filter(post => post.category === filter);
  const [lead, ...rest] = filtered;

  return (
    <div className="tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-16 tw-pt-4">
        <div className="tw-mb-10 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-px-4 tw-py-1.5 tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-transition-colors ${
                filter === f.key
                  ? 'tw-border-primary-700 tw-bg-primary-700 tw-text-white dark:tw-border-primary-300 dark:tw-bg-primary-300 dark:tw-text-[#0E1A16]'
                  : 'tw-border-[#E7E0CF] tw-bg-white tw-text-[#5B6A63] hover:tw-border-primary-500 hover:tw-text-primary-700 dark:tw-border-[#26382F] dark:tw-bg-[#15231E] dark:tw-text-[#A7B4AC] dark:hover:tw-text-primary-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {lead && <LeadCard post={lead} />}
        <div className={GRID}>
          {rest.map(post => (
            <BlogPostCard key={post.key} post={post} />
          ))}
        </div>
      </div>
    </div>
  );
}
